import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Grid, Tooltip, IconButton, Chip } from '@mui/material';
import { HeaderButton, PageContainer } from '../../Global/Components';
import CircularProgress from '@mui/material/CircularProgress';
import StateManager from '../../Global/StateManager';
import PageHeader from '../../Global/PageHeader';
import { AddRounded, PublishRounded } from '@mui/icons-material';
import { VideosIcon } from '../../Global/Icons';
import { PostListHomeView } from '../components/post-list-home-view';
import EditPost from '../components/post-edit';
import TagsFilter from '../components/TagFilter';
import { isEmpty } from 'lodash';
import axios from 'axios';

function debounce(func, delay) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

const titles = {
  undefined: 'All videos',
  all: 'All videos',
  tutorials: 'Application tutorials',
  useCases: 'Use cases',
  examples: 'Customer examples',
  features: 'Features',
  updates: 'Updates',
  podcasts: 'Podcasts',
};

export default function VideoSection({ version }) {
  const [dialog, setDialog] = useState(false);
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchPattern, setSearchPattern] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const limit = 9;
  //eslint-disable-next-line
  const fetchVideos = useCallback(
    debounce((tags, pattern, pageNum) => {
      setLoading(true);
      axios
        .post('/help/getVideos', {
          selectedTags: tags,
          searchPattern: pattern,
          version: version,
          page: pageNum,
          limit: limit,
        })
        .then((response) => {
          setPosts(response.data.videos);
          setTags(response.data.tags);
          setTotalPages(response.data.pagination.totalPages);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          StateManager.setAxiosErrorAlert(error);
        });
    }, 500),
    //eslint-disable-next-line
    [],
  );

  const { user } = useSelector(({ profile }) => profile);
  let unwatchedVideos = user?.unwatchedVideos || [];
  const isSuperAdmin = user?.isSuperAdmin;

  useEffect(() => {
    fetchVideos(selectedTags, searchPattern, page);
  }, [selectedTags, searchPattern, page, fetchVideos]);

  function createVideo(formData) {
    axios
      .post('/help/createVideo', formData)
      .then((data) => {
        setPosts([...posts, data.data]);
        unwatchedVideos.push({ _id: data.data._id });
        setDialog(false);
      })
      .catch((error) => StateManager.setAxiosErrorAlert(error));
  }

  function onEdit(data, newTags) {
    setPosts((prevPosts) => prevPosts.map((post) => (post._id === data._id ? data : post)));
    if (!isEmpty(newTags)) {
      setTags((prevTags) => prevTags.filter((tag) => !newTags.includes(tag)));
    }
  }

  function onDelete(postId) {
    setPosts((prevPosts) => prevPosts.filter((post) => post._id !== postId));
  }

  function uploadFile(file) {
    const data = new FormData();
    data.append('file', file);
    setUploading(true);
    axios
      .post(`/help/importExcelFile`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => {
        setUploading(false);
        StateManager.setSuccessAlert(`${res.data.inserted} videos have been created`);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setUploading(false);
      });
  }

  return (
    <PageContainer>
      <Grid container>
        <PageHeader
          title={titles[version]}
          onSearch={setSearchPattern}
          sx={{ mt: 0 }}
          icon={<VideosIcon />}
          options={
            <>
              {isSuperAdmin && (
                <HeaderButton
                  color="primary"
                  text="Add a video"
                  icon={<AddRounded fontSize="large" />}
                  onClick={() => setDialog(true)}
                />
              )}
              {isSuperAdmin && (
                <Tooltip title="Upload assets from excel">
                  <IconButton
                    component="label"
                    onClick={(event) => {
                      event.target.value = null;
                    }}
                  >
                    <input
                      style={{ display: 'none' }}
                      type="file"
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={(e) => {
                        if (!e.target.files || e.target.files.length === 0) return;
                        uploadFile(e.target.files[0]);
                      }}
                    />
                    {uploading ? (
                      <CircularProgress color="primary" size={24} />
                    ) : (
                      <PublishRounded color="textSecondary" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              {/* <TagsFilter selectedFilters={selectedTags} setSelectedFilters={setSelectedTags} tagsInfo={tags} /> */}
            </>
          }
        />
        <Grid container item ml={1}>
          {tags.length
            ? tags.map((tag) => (
                <Chip
                  key={tag.title}
                  variant={selectedTags.includes(tag.title) ? 'filled' : 'outlined'}
                  label={tag.title}
                  onClick={() =>
                    selectedTags.includes(tag.title)
                      ? setSelectedTags([...selectedTags.filter((item) => item !== tag.title)])
                      : setSelectedTags([...selectedTags, tag.title])
                  }
                  sx={{ mx: 0.5, mt: 1 }}
                />
              ))
            : null}
        </Grid>
        <PostListHomeView
          posts={posts}
          isSuperAdmin={isSuperAdmin}
          onEdit={onEdit}
          onDelete={onDelete}
          tags={tags}
          categories={titles}
          page={page}
          totalPages={totalPages}
          onPageChange={(newPage) => {
            setLoading(true);
            setPage(newPage);
          }}
          loading={loading}
          unwatchedVideos={unwatchedVideos}
        />

        <EditPost
          open={dialog}
          onClose={(data, formData) => {
            if (formData) createVideo(formData);
            setDialog(false);
          }}
          tags={tags}
          categories={titles}
        />
      </Grid>
    </PageContainer>
  );
}
